import React, { useState, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Stack,
  Typography,
} from '@mui/material'
import FolderIcon from '@mui/icons-material/Folder'
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder'
import { GenNode } from '../../types/carbon/models/GenNode'
import { UserTOCEntry } from '../../hooks/useUserTOC'

export interface FolderPickerDialogProps {
  open: boolean
  onClose: () => void
  onSelect: (folderPath: string, destination: 'user' | 'exec') => void
  tocNodes: GenNode[]
  userEmail: string
  isManager?: boolean
  execOnly?: boolean
  userOnly?: boolean
  userFolders?: UserTOCEntry[]
  title?: string
  // forbidRoot hides the "My Tables (root)" and "Exec (root)" entries and
  // disables Select unless the user picked a specific folder or named a new
  // subfolder. Used by the Regenerate Tables flow to keep batch output
  // contained in its own folder (issue #789).
  forbidRoot?: boolean
}

/** Every My Tables folder in user.toc, nested ones as 'A/B', in tree order. */
export const collectUserFolders = (entries: UserTOCEntry[], prefix: string, depth: number): FolderEntry[] => {
  const result: FolderEntry[] = []
  for (const entry of entries) {
    if (Array.isArray(entry.children)) {
      const path = prefix ? `${prefix}/${entry.name}` : entry.name
      result.push({ path, label: entry.name, destination: 'user', depth })
      result.push(...collectUserFolders(entry.children, path, depth + 1))
    }
  }
  return result
}

export interface FolderEntry {
  path: string
  label: string
  destination: 'user' | 'exec'
  depth: number
}

const collectFolders = (nodes: GenNode[], prefix: string, destination: 'user' | 'exec', depth: number): FolderEntry[] => {
  const result: FolderEntry[] = []
  for (const node of nodes) {
    if (node.type === 'Folder') {
      const path = prefix ? `${prefix}/${node.value1 || ''}` : (node.value1 || '')
      result.push({ path, label: node.value1 || '', destination, depth })
      if (node.children) {
        result.push(...collectFolders(node.children, path, destination, depth + 1))
      }
    } else if (node.children) {
      // Traverse non-Folder nodes (e.g. Section) to find nested Folders
      result.push(...collectFolders(node.children, prefix, destination, depth))
    }
  }
  return result
}

const FolderPickerDialog: React.FC<FolderPickerDialogProps> = ({
  open,
  onClose,
  onSelect,
  tocNodes,
  userEmail,
  isManager = false,
  execOnly = false,
  userOnly = false,
  userFolders = [],
  title,
  forbidRoot = false,
}) => {
  const [selected, setSelected] = useState<string>('')
  const [selectedDest, setSelectedDest] = useState<'user' | 'exec'>(execOnly ? 'exec' : 'user')
  const [newFolderName, setNewFolderName] = useState('')
  const [showNewFolder, setShowNewFolder] = useState(false)

  const folders = useMemo(() => {
    const result: FolderEntry[] = []
    // User folders (hidden when execOnly)
    if (!execOnly) {
      if (!forbidRoot) {
        result.push({ path: '', label: `My Tables (${userEmail})`, destination: 'user', depth: 0 })
      }
      result.push(...collectUserFolders(userFolders, '', 1))
    }
    // Exec folders (hidden when userOnly; otherwise visible to managers or when execOnly)
    if (!userOnly && (isManager || execOnly)) {
      if (!forbidRoot) {
        result.push({ path: '', label: 'Exec (root)', destination: 'exec', depth: 0 })
      }
      result.push(...collectFolders(tocNodes, '', 'exec', 1))
    }
    return result
  }, [tocNodes, userEmail, isManager, execOnly, userOnly, userFolders, forbidRoot])

  const handleConfirm = () => {
    let folderPath = selected
    if (showNewFolder && newFolderName.trim()) {
      folderPath = selected ? `${selected}/${newFolderName.trim()}` : newFolderName.trim()
    }
    onSelect(folderPath, selectedDest)
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title || 'Select Destination Folder'}</DialogTitle>
      <DialogContent>
        <List dense sx={{ maxHeight: 300, overflow: 'auto', mb: 1 }}>
          {folders.map((f, i) => (
            <ListItemButton
              key={`${f.destination}-${f.path}-${i}`}
              selected={selected === f.path && selectedDest === f.destination}
              onClick={() => { setSelected(f.path); setSelectedDest(f.destination) }}
              sx={{ pl: 2 + f.depth * 2 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <FolderIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={f.label} />
            </ListItemButton>
          ))}
        </List>

        <Stack direction="row" spacing={1} alignItems="center">
          {showNewFolder ? (
            <>
              <TextField
                value={newFolderName}
                onChange={e => setNewFolderName(e.target.value)}
                placeholder="New folder name"
                size="small"
                sx={{ flex: 1 }}
                autoFocus
              />
              <Button size="small" onClick={() => setShowNewFolder(false)}>Cancel</Button>
            </>
          ) : (
            <Button
              size="small"
              startIcon={<CreateNewFolderIcon />}
              onClick={() => setShowNewFolder(true)}
            >
              New Subfolder
            </Button>
          )}
        </Stack>

        {selected || selectedDest ? (
          <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
            Saving to: {selectedDest === 'exec' ? 'Exec' : 'User'}/{selected || '(root)'}{showNewFolder && newFolderName ? `/${newFolderName}` : ''}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="info"
          onClick={handleConfirm}
          disabled={forbidRoot && !selected && !(showNewFolder && newFolderName.trim())}
        >
          Select
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default FolderPickerDialog
